import { Head, Link, useForm } from "@inertiajs/react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ArrowLeft, Plus } from "lucide-react"
import AppSidebarLayout from "@/layouts/app/app-sidebar-layout"
import { expensesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
  },
  {
    title: "Expenses",
    href: expensesPath(),
  },
  {
    title: "Add Expense",
    href: "/expenses/new",
  },
]

interface ExpenseFormData {
  expense: {
    amount: number
    category: string
    description: string
    expense_date: string
  }
}

interface ExpensesNewProps {
  expense: {
    amount: number
    category: string
    description: string
    expense_date: string
  }
  categories: string[]
  errors?: string[]
}

export default function ExpensesNew({
  expense,
  categories,
  errors = [],
}: ExpensesNewProps) {
  const { data, setData, post, processing } = useForm<ExpenseFormData>({
    expense: {
      amount: expense.amount,
      category: expense.category || "",
      description: expense.description || "",
      expense_date:
        expense.expense_date || new Date().toISOString().split("T")[0],
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(expensesPath())
  }

  return (
    <AppSidebarLayout breadcrumbs={breadcrumbs}>
      <Head title="Add Expense" />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={expensesPath()}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Expenses
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Add New Expense</h1>
            <p className="text-muted-foreground">
              Track your spending to stay on top of your finances
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Expense Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Amount */}
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount *</Label>
                  <div className="relative">
                    <div className="text-muted-foreground absolute top-1.5 left-3">
                      $
                    </div>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="pl-8"
                      value={data.expense.amount}
                      onChange={(e) =>
                        setData("expense", {
                          ...data.expense,
                          amount: parseFloat(e.target.value),
                        })
                      }
                      required
                    />
                  </div>
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={data.expense.category}
                    onValueChange={(value) =>
                      setData("expense", {
                        ...data.expense,
                        category: value,
                      })
                    }
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="What did you spend on? (optional)"
                    value={data.expense.description}
                    onChange={(e) =>
                      setData("expense", {
                        ...data.expense,
                        description: e.target.value,
                      })
                    }
                    rows={3}
                  />
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <Label htmlFor="expense_date">Date *</Label>
                  <Input
                    id="expense_date"
                    type="date"
                    value={data.expense.expense_date}
                    onChange={(e) =>
                      setData("expense", {
                        ...data.expense,
                        expense_date: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                {/* Error Messages */}
                {errors.length > 0 && (
                  <div className="bg-destructive/15 rounded-md p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-destructive text-sm font-medium">
                          There were errors with your submission:
                        </h3>
                        <div className="text-destructive mt-2 text-sm">
                          <ul role="list" className="list-disc space-y-1 pl-5">
                            {errors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex gap-4 pt-4">
                  <Button type="submit" disabled={processing}>
                    {processing ? "Creating..." : "Create Expense"}
                  </Button>
                  <Button variant="outline" asChild>
                    <Link href={expensesPath()}>Cancel</Link>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Quick Tips */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">💡 Quick Tips</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-2 text-sm">
              <p>
                • Be specific with descriptions to help track spending patterns
              </p>
              <p>• Use consistent categories for better expense analysis</p>
              <p>• Regular expense tracking helps with FIRE goal achievement</p>
              <p>• You can edit or delete expenses later if needed</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppSidebarLayout>
  )
}
